import { useEffect, useRef, useState } from "react";
import { FileUp, FileCheck2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const SingleUpload = ({ order, onDone }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("PDF files only.");
      return;
    }
    setBusy(true);
    try {
      const pdf_base64 = await toBase64(file);
      await appApi.post(`admin/orders/${order.id}/label`, { pdf_base64 });
      toast.success(`Label uploaded for ${order.awb} — merchant can download it now.`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="application/pdf" className="hidden" data-testid={`label-file-${order.awb}`} onChange={onFile} />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`label-upload-${order.awb}`}
        className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-obsidian bg-acid text-white hover:bg-obsidian transition-colors duration-150 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {busy ? "…" : "Upload PDF"}
      </button>
    </>
  );
};

const LabelsTable = ({ endpoint, tabKey }) => {
  const [orders, setOrders] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkRef = useRef(null);

  const load = () => {
    setOrders(null);
    appApi.get(endpoint).then((d) => setOrders(d.orders || [])).catch(() => setOrders([]));
  };
  useEffect(load, [endpoint]);

  const needing = (orders || []).filter((o) => !o.has_admin_label);
  const done = (orders || []).filter((o) => o.has_admin_label);

  const bulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBulkBusy(true);
    let ok = 0, miss = 0;
    for (const file of files) {
      if (file.type !== "application/pdf") { miss++; continue; }
      const match = needing.find((o) => file.name.includes(o.awb));
      if (!match) {
        toast.error(`No order AWB found in "${file.name}" — name files like label-<AWB>.pdf`);
        miss++;
        continue;
      }
      try {
        const pdf_base64 = await toBase64(file);
        await appApi.post(`admin/orders/${match.id}/label`, { pdf_base64 });
        ok++;
      } catch (err) {
        toast.error(`${match.awb}: ${formatApiErrorDetail(err.response?.data?.detail)}`);
        miss++;
      }
    }
    setBulkBusy(false);
    toast.success(`Bulk upload done — ${ok} label(s) attached${miss ? `, ${miss} skipped` : ""}.`);
    load();
  };

  const Row = ({ o }) => (
    <tr className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100" data-testid={`label-row-${o.awb}`}>
      <td className="px-4 py-3 font-mono text-xs font-bold">{o.awb}</td>
      <td className="px-4 py-3 text-xs text-zinc-600">
        {o.merchant?.name || o.merchant_name || "—"}
        <span className="block text-zinc-400 font-mono">{o.merchant?.email || o.merchant_email || ""}</span>
      </td>
      <td className="px-4 py-3 text-zinc-600">{o.courier_name}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{o.pickup?.pincode} → {o.delivery?.pincode}</td>
      <td className="px-4 py-3 font-mono text-xs font-bold">{o.rate != null ? inr(o.rate) : "—"}</td>
      <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(o.created_at)}</td>
      <td className="px-4 py-3">
        {o.has_admin_label ? (
          <span data-testid={`label-done-${o.awb}`} className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider text-green-700">
            <FileCheck2 className="w-3.5 h-3.5" /> Uploaded
          </span>
        ) : (
          <SingleUpload order={o} onDone={load} />
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid={`${tabKey}-counts`}>
          {orders ? `${needing.length} need labels · ${done.length} uploaded` : "Loading…"}
        </p>
        <div>
          <input ref={bulkRef} type="file" accept="application/pdf" multiple className="hidden" data-testid={`${tabKey}-bulk-input`} onChange={bulkUpload} />
          <button
            onClick={() => bulkRef.current?.click()}
            disabled={bulkBusy || needing.length === 0}
            data-testid={`${tabKey}-bulk-upload`}
            className="inline-flex items-center gap-2 bg-obsidian text-sky-300 text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#2563EB] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-50"
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            Bulk upload PDFs (name = AWB)
          </button>
        </div>
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]" data-testid={`${tabKey}-table`}>
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["AWB", "Merchant", "Courier", "Route", "Rate", "Status", "Created", "Label"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...needing, ...done].map((o) => <Row key={o.id || o.awb} o={o} />)}
          </tbody>
        </table>
        {orders && orders.length === 0 && (
          <p className="px-5 py-10 text-sm text-zinc-500" data-testid={`${tabKey}-empty`}>All labels are up to date.</p>
        )}
        {!orders && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading orders…</p>}
      </div>
    </div>
  );
};

export default function AdminLabels() {
  const [tab, setTab] = useState("amazon-b2c");

  return (
    <div data-testid="admin-labels-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Shipping Labels</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Upload courier labels for merchants — their download buttons light up instantly on web and app.
        </p>
      </div>

      <div className="flex gap-0 border-2 border-obsidian w-fit bg-white flex-wrap" data-testid="labels-tabs">
        {[
          { k: "amazon-b2c", l: "Amazon B2C Labels", ep: "admin/orders/amazon-b2c" },
          { k: "b2b", l: "B2B Freight Labels", ep: "admin/orders/b2b" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`labels-tab-${t.k}`}
            className={`px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${tab === t.k ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <LabelsTable key={tab} tabKey={tab} endpoint={tab === "b2b" ? "admin/orders/b2b" : "admin/orders/amazon-b2c"} />
    </div>
  );
}
